import { DollarSign, Gift, Trophy, Clock, CheckCircle2, XCircle, Star, Zap } from "lucide-react";

import { useTranslation } from 'react-i18next';
function TierBadge({ tier, rate }) {
  const colors = {
    Bronze: "border-amber-700/50 bg-amber-700/10 text-amber-600",
    Silver: "border-zinc-400/50 bg-zinc-400/10 text-zinc-300",
    Gold: "border-amber-400/50 bg-amber-400/10 text-amber-400",
  };
  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1.5 border ${colors[tier] || colors.Bronze}`}>
      <Star className="w-3.5 h-3.5" />
      <span className="font-mono text-[11px] uppercase tracking-wider font-bold">{tier}</span>
      <span className="font-mono text-[10px] opacity-70">${rate}/ref</span>
    </div>
  );
}

function MilestoneCard({ count, bonus, achieved, icon: Icon }) {
  return (
    <div className={`border p-4 flex items-center gap-4 ${achieved ? "border-amber-500/30 bg-amber-500/5" : "border-zinc-800 bg-zinc-950/30"}`}>
      <div className={`w-10 h-10 flex items-center justify-center ${achieved ? "bg-amber-500/20" : "bg-zinc-800"}`}>
        <Icon className={`w-5 h-5 ${achieved ? "text-amber-400" : "text-zinc-600"}`} />
      </div>
      <div className="flex-1">
        <div className="font-mono text-[12px] text-white">{count} Referrals</div>
        <div className="font-mono text-[10px] text-zinc-500">${bonus} bonus</div>
      </div>
      {achieved ? (
        <span className="font-mono text-[10px] text-emerald-400 flex items-center gap-1">
          <CheckCircle2 className="w-3 h-3" /> Unlocked
        </span>
      ) : (
        <span className="font-mono text-[10px] text-zinc-600 flex items-center gap-1">
          <Clock className="w-3 h-3" /> Locked
        </span>
      )}
    </div>
  );
}

function RewardRow({ entry }) {
  const statusConfig = {
    credited: { icon: CheckCircle2, color: "text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/20" },
    pending: { icon: Clock, color: "text-amber-400", bg: "bg-amber-500/10 border-amber-500/20" },
    expired: { icon: XCircle, color: "text-zinc-500", bg: "bg-zinc-800/50 border-zinc-700/20" },
  };
  const config = statusConfig[entry.status] || statusConfig.pending;
  const StatusIcon = config.icon;

  return (
    <div className={`flex items-center justify-between p-3 border ${config.bg}`}>
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-8 h-8 bg-zinc-800 flex items-center justify-center shrink-0">
          {entry.type === "milestone" ? (
            <Trophy className="w-4 h-4 text-amber-400" />
          ) : (
            <Gift className="w-4 h-4 text-zinc-400" />
          )}
        </div>
        <div className="min-w-0">
          <div className="font-mono text-[11px] text-zinc-300 truncate">{entry.description}</div>
          <div className="font-mono text-[9px] text-zinc-600">
            {new Date(entry.date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className="font-mono text-[12px] text-white font-bold">+${entry.amount}</span>
        <StatusIcon className={`w-3.5 h-3.5 ${config.color}`} />
      </div>
    </div>
  );
}

export default function RewardsTracker({ stats, tierInfo, rewardHistory }) {
  const { t } = useTranslation();
  const milestones = [
    { count: 5, bonus: 100, icon: Gift },
    { count: 10, bonus: 250, icon: Trophy },
    { count: 25, bonus: 1000, icon: Zap },
  ];

  return (
    <div className="space-y-8">
      {/* Current Tier + Balance */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="border border-zinc-800 bg-zinc-950/30 p-6">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 block mb-3">{t('rewards_tracker.current_tier')}</span>
          <TierBadge tier={tierInfo.current} rate={tierInfo.currentRate} />
          {tierInfo.next && (
            <p className="font-mono text-[11px] text-zinc-500 mt-3">
              Earn ${tierInfo.nextRate}/referral at {tierInfo.next} tier ({tierInfo.referralsToNext} more referrals)
            </p>
          )}
          {!tierInfo.next && (
            <p className="font-mono text-[11px] text-amber-400 mt-3 flex items-center gap-1">
              <Star className="w-3 h-3" /> Maximum tier reached!
            </p>
          )}
        </div>

        <div className="border border-amber-500/30 bg-gradient-to-br from-amber-500/5 to-transparent p-6">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 block mb-3">{t('rewards_tracker.credit_balance')}</span>
          <div className="font-display text-3xl text-amber-400">${stats.credits}</div>
          <div className="font-mono text-[11px] text-zinc-500 mt-2">
            ${stats.baseCredits} referral rewards + ${stats.milestoneBonuses} milestone bonuses
          </div>
        </div>
      </div>

      {/* Tier Breakdown */}
      <div className="border border-zinc-800 bg-zinc-950/30 p-6">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 block mb-4">{t('rewards_tracker.tier_rewards')}</span>
        <div className="grid grid-cols-3 gap-3">
          <div className={`p-3 border text-center ${tierInfo.current === "Bronze" ? "border-amber-500/30 bg-amber-500/5" : "border-zinc-800"}`}>
            <div className="font-display text-lg text-amber-600">Bronze</div>
            <div className="font-mono text-[11px] text-zinc-400">$25/referral</div>
            <div className="font-mono text-[9px] text-zinc-600 mt-1">1-5 refs</div>
          </div>
          <div className={`p-3 border text-center ${tierInfo.current === "Silver" ? "border-amber-500/30 bg-amber-500/5" : "border-zinc-800"}`}>
            <div className="font-display text-lg text-zinc-300">Silver</div>
            <div className="font-mono text-[11px] text-zinc-400">$35/referral</div>
            <div className="font-mono text-[9px] text-zinc-600 mt-1">6-10 refs</div>
          </div>
          <div className={`p-3 border text-center ${tierInfo.current === "Gold" ? "border-amber-500/30 bg-amber-500/5" : "border-zinc-800"}`}>
            <div className="font-display text-lg text-amber-400">Gold</div>
            <div className="font-mono text-[11px] text-zinc-400">$50/referral</div>
            <div className="font-mono text-[9px] text-zinc-600 mt-1">11+ refs</div>
          </div>
        </div>
      </div>

      {/* Milestones */}
      <div>
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 block mb-3">{t('rewards_tracker.milestone_bonuses')}</span>
        <div className="grid md:grid-cols-3 gap-3">
          {milestones.map((m) => (
            <MilestoneCard key={m.count} count={m.count} bonus={m.bonus} achieved={stats.convertedCount >= m.count} icon={m.icon} />
          ))}
        </div>
      </div>

      {/* Reward History */}
      {rewardHistory.length > 0 && (
        <div>
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 block mb-3">
            Reward History ({rewardHistory.length})
          </span>
          <div className="border border-zinc-800 divide-y divide-zinc-800">
            {rewardHistory.map((entry) => (
              <RewardRow key={entry.id} entry={entry} />
            ))}
          </div>
        </div>
      )}

      {rewardHistory.length === 0 && (
        <div className="border border-zinc-800 bg-zinc-950/30 p-8 text-center">
          <Gift className="w-8 h-8 text-zinc-700 mx-auto mb-3" />
          <p className="font-mono text-[12px] text-zinc-500">{t('rewards_tracker.no_rewards_yet_start_referring_friends_to_earn')}</p>
        </div>
      )}
    </div>
  );
}
